import React, { useState, useEffect, useCallback, useMemo } from 'react';
import APIService from '../../services/api';

/**
 * Disponibilités pour les entretiens.
 *
 * Ces réglages ne décrivent pas un agenda : ils décrivent la RÈGLE à partir
 * de laquelle les créneaux proposés au candidat sont fabriqués. D'où le parti
 * pris de l'écran — une phrase qui se lit d'un trait, et l'aperçu de ce que
 * le candidat verra réellement. Régler « granularité 15 min » sans voir le
 * résultat ne veut rien dire.
 *
 * Routes : GET/PUT /config/entretiens — fusion partielle, refus 400 + champ.
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

const LBL = {
  fontFamily: 'var(--erp-font-mono)', fontSize: '9.5px', fontWeight: 700,
  letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--erp-text-tertiary)',
};
/* Semaine ISO : lundi = 1, comme le serveur et comme tout le monde ici. */
const JOURS = [[1, 'Lun'], [2, 'Mar'], [3, 'Mer'], [4, 'Jeu'], [5, 'Ven'], [6, 'Sam'], [7, 'Dim']];
/* Le fuseau dans lequel se lisent les heures d'ouverture. Sans lui, « 09:00 »
   était neuf heures à Greenwich : le candidat se voyait proposer 11 h – 20 h
   à Genève l'été, sans que personne comprenne d'où ça sortait. */
const FUSEAUX = ['Europe/Zurich', 'Europe/Paris', 'Europe/Brussels', 'Europe/London', 'Europe/Lisbon',
  'Europe/Madrid', 'Europe/Berlin', 'Africa/Casablanca', 'Africa/Tunis', 'America/Montreal'];
const fuseauNavigateur = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Zurich'; } catch { return 'Europe/Zurich'; } };
const VIDE = { jours: [1, 2, 3, 4, 5], fuseau: 'Europe/Zurich', heure_debut: '09:00', heure_fin: '17:00', duree_min: 45, granularite_min: 30, delai_min_h: 0, horizon_jours: 14, max_creneaux: 12 };
const num = (v) => (v === '' || v == null ? '' : Number(v));
const mn = (v) => { const [h, m] = String(v || '0:0').split(':').map(Number); return (h || 0) * 60 + (m || 0); };

export default function EntretiensConfig({ showNotification }) {
  const [c, setC] = useState(null);
  const [orig, setOrig] = useState(null);
  const [absent, setAbsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [champErreur, setChampErreur] = useState(null);

  const charger = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/config/entretiens`, { headers: authH() });
      if ([404, 405, 501].includes(r.status)) { setAbsent(true); setC(VIDE); setOrig(VIDE); return; }
      const j = await r.json().catch(() => ({}));
      const raw = j.data ?? j;
      const d = raw.value ?? raw;
      const v = { ...VIDE, ...Object.fromEntries(Object.entries(d).filter(([, x]) => x != null)) };
      v.jours = Array.isArray(v.jours) && v.jours.length ? v.jours.map(Number) : VIDE.jours;
      setC(v); setOrig(v);
    } catch { setC(VIDE); setOrig(VIDE); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const dirty = c && orig && JSON.stringify(c) !== JSON.stringify(orig);
  const maj = (patch) => { setC(v => ({ ...v, ...patch })); if (champErreur) setChampErreur(null); };
  const basculerJour = (j) => maj({ jours: c.jours.includes(j) ? c.jours.filter(x => x !== j) : [...c.jours, j].sort((a, b) => a - b) });

  const enregistrer = async () => {
    setBusy(true); setChampErreur(null);
    try {
      const r = await fetch(`${BASE}/config/entretiens`, { method: 'PUT', headers: jsonH(), body: JSON.stringify(c) });
      const j = await r.json().catch(() => ({}));
      if ([404, 405, 501].includes(r.status) && !j.error) {
        showNotification?.('Les disponibilités arrivent côté serveur — rien n\'a été enregistré.', 'info');
        return;
      }
      if (!r.ok || j.success === false) {
        const msg = j.error || j.message || `HTTP ${r.status}`;
        if (r.status === 400 && j.champ) { setChampErreur({ champ: String(j.champ), message: msg }); return; }
        throw new Error(msg);
      }
      setOrig(c);
      showNotification?.(j.message || 'Disponibilités enregistrées — les prochains créneaux les suivront.', 'success');
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  /* Aperçu : les créneaux d'une journée type, tels que la règle les produit.
     C'est la seule façon de comprendre ce qu'on vient de régler. */
  const apercu = useMemo(() => {
    if (!c) return [];
    const debut = mn(c.heure_debut), fin = mn(c.heure_fin);
    const pas = Number(c.granularite_min) || 30, duree = Number(c.duree_min) || 30;
    if (!(fin > debut) || pas <= 0) return [];
    const out = [];
    for (let t = debut; t + duree <= fin && out.length < 40; t += pas) {
      out.push(`${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`);
    }
    return out;
  }, [c]);

  const champ = (cle, label, props = {}) => {
    const faux = champErreur?.champ === cle;
    return (
      <label className="rdvcfg-champ">
        <span style={LBL}>{label}</span>
        <input className="erp-field erp-mono" type={props.type || 'number'} min={props.min} max={props.max} step={props.step}
          value={c[cle] ?? ''} aria-invalid={faux || undefined}
          onChange={e => maj({ [cle]: props.type === 'time' ? e.target.value : num(e.target.value) })}
          style={{ width: '100%', textAlign: props.type === 'time' ? 'left' : 'right', ...(faux ? { borderColor: 'var(--danger-bd)' } : null) }} />
        {props.aide && !faux && <small>{props.aide}</small>}
        {faux && <small role="alert" style={{ color: 'var(--danger-fg)' }}>{champErreur.message}</small>}
      </label>
    );
  };

  if (c === null) return <div style={{ padding: '20px', color: 'var(--erp-text-tertiary)', fontSize: '13px' }}><BI icon="arrow-repeat" /> Chargement…</div>;

  const joursLibelles = JOURS.filter(([j]) => c.jours.includes(j)).map(([, l]) => l);
  // « Fermé aujourd'hui » : le délai minimum repousse le premier créneau
  // au-delà de l'heure de fermeture du jour.
  const maintenantMin = new Date().getHours() * 60 + new Date().getMinutes();
  const ferme = maintenantMin + Number(c.delai_min_h || 0) * 60 + Number(c.duree_min || 0) > mn(c.heure_fin);

  return (
    <div className="rdvcfg">
      <p className="rdvcfg-intro">
        Ces réglages ne décrivent pas un agenda : ils décrivent <strong>la règle</strong> à partir de laquelle
        les créneaux proposés au candidat sont fabriqués.
      </p>

      {absent && <div className="rdvcfg-note">Les disponibilités arrivent côté serveur. L'écran est prêt : vos réglages aboutiront dès la mise en ligne.</div>}
      {champErreur && !champErreur.champ && <div className="rdvcfg-erreur" role="alert">{champErreur.message}</div>}

      {/* La règle, en une phrase — c'est elle qu'on relit, pas huit champs. */}
      <p className="rdvcfg-phrase">
        Les entretiens durent <b>{c.duree_min || '—'} min</b>, {joursLibelles.length ? <>le <b>{joursLibelles.join(', ')}</b></> : <b>aucun jour choisi</b>}, entre <b>{c.heure_debut}</b> et <b>{c.heure_fin}</b> (<b>{c.fuseau || 'Europe/Zurich'}</b>).
        On propose <b>{c.max_creneaux || '—'}</b> créneaux au plus, au moins <b>{c.delai_min_h || 0} h</b> à l'avance, sur <b>{c.horizon_jours || '—'} jours</b>.
      </p>
      {/* Un délai qui dépasse ce qu'il reste d'aujourd'hui FERME la journée en
          cours — c'est la question « pourquoi il ne propose pas aujourd'hui ? »,
          qui n'avait aucune réponse à l'écran. */}
      {Number(c.delai_min_h) > 0 && ferme && (
        <p className="rdvcfg-note">
          Avec <b>{c.delai_min_h} h</b> de délai minimum, <b>aucun créneau n'est proposé aujourd'hui</b> :
          le premier tombe {Number(c.delai_min_h) >= 48 ? `dans ${Math.floor(Number(c.delai_min_h) / 24)} jours` : 'demain'}.
          Mettez ce délai à <b>0</b> pour ouvrir la journée en cours.
        </p>
      )}

      <fieldset className="rdvcfg-jours">
        <legend style={LBL}>Jours ouverts</legend>
        <div>
          {JOURS.map(([j, l]) => (
            <button type="button" key={j} className={c.jours.includes(j) ? 'is-on' : ''} aria-pressed={c.jours.includes(j)} onClick={() => basculerJour(j)}>{l}</button>
          ))}
        </div>
      </fieldset>

      <div className="rdvcfg-grille">
        <label className="rdvcfg-champ">
          <span style={LBL}>Fuseau des heures</span>
          <select className="erp-field erp-mono" value={c.fuseau || 'Europe/Zurich'}
            aria-invalid={champErreur?.champ === 'fuseau' || undefined}
            onChange={e => maj({ fuseau: e.target.value })}
            style={{ width: '100%', ...(champErreur?.champ === 'fuseau' ? { borderColor: 'var(--danger-bd)' } : null) }}>
            {[...new Set([fuseauNavigateur(), ...FUSEAUX, c.fuseau].filter(Boolean))].map(f => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
          {champErreur?.champ === 'fuseau'
            ? <small role="alert" style={{ color: 'var(--danger-fg)' }}>{champErreur.message}</small>
            : <small>Les heures ci-dessous se lisent dans ce fuseau.</small>}
        </label>
        {champ('heure_debut', 'Ouverture', { type: 'time' })}
        {champ('heure_fin', 'Fermeture', { type: 'time' })}
        {champ('duree_min', 'Durée (min)', { min: 5, step: 5 })}
        {champ('granularite_min', 'Pas entre créneaux (min)', { min: 5, step: 5, aide: 'Un pas plus court propose plus d\'heures.' })}
        {champ('delai_min_h', 'Délai minimum (h)', { min: 0, aide: 'Rien de proposé avant ce délai — 24 h ferme la journée en cours.' })}
        {champ('horizon_jours', 'Horizon (jours)', { min: 1, aide: 'Jusqu\'où on cherche des créneaux.' })}
        {champ('max_creneaux', 'Créneaux proposés au plus', { min: 1 })}
      </div>

      <div className="rdvcfg-apercu">
        <span style={LBL}>Ce que le candidat verrait pour une journée</span>
        {apercu.length ? (
          <div className="rdvcfg-apercu-liste">
            {apercu.slice(0, Number(c.max_creneaux) || apercu.length).map(h => <span key={h} className="erp-mono">{h}</span>)}
            {apercu.length > (Number(c.max_creneaux) || apercu.length) && (
              <em>+{apercu.length - (Number(c.max_creneaux) || 0)} possibles, non proposés — la limite s'applique sur l'ensemble des jours.</em>
            )}
          </div>
        ) : (
          <p className="rdvcfg-apercu-vide">Aucun créneau possible : vérifiez l'ouverture, la fermeture et la durée.</p>
        )}
      </div>

      <div className="rdvcfg-pied">
        <span>{dirty ? 'Modifications non enregistrées.' : 'Disponibilités à jour.'}</span>
        <button type="button" className="erp-btn erp-btn--primary" onClick={enregistrer} disabled={busy || !dirty}
          style={(busy || !dirty) ? { opacity: 0.5 } : undefined}>
          {busy ? 'Enregistrement…' : 'Enregistrer les disponibilités'}
        </button>
      </div>
    </div>
  );
}
